// **Which surface am I talking to?** (rounds-slice implementation-review **F1**.)
//
// The server's `GameMode` is an operator config with a single mount-time fork (design **D1**):
// under `Live` it serves `/ws` + the lobby routes and no rounds routes; under `Rounds` it serves
// the rounds routes and no `/ws` at all. There is no request that reports the mode — deliberately
// (`server-config`: "not selectable by any request") — so the client cannot ask, and a client that
// guesses wrong is dead on arrival either way: a live boot against a rounds server dials a `/ws`
// that is not mounted, and a rounds boot against a live server mints against a `/rounds/session`
// that is not mounted.
//
// So mode is resolved from three signals, most explicit first:
//
//   1. `?mode=rounds` / `?mode=live` — an explicit, per-load override. Also the escape hatch when
//      a deployment sits behind something that mangles the probe below.
//   2. The build-time `VITE_GAME_MODE` — for a deployment that ships a bundle it has already
//      decided the mode of.
//   3. **A probe of the surface actually served.** `GET /rounds/session` is answered by a rounds
//      deployment (the route exists, POST-only, so `405`) and 404s on a live one (the route is not
//      mounted at all). Any answer that is not a 404 means the rounds surface is there.
//
// An inconclusive probe (unreachable, a proxy error, a timeout) resolves to **live**, so the live
// boot path — which is the one that must keep working exactly as it shipped — is never displaced
// by an ambiguous signal.

import { PVP_PROBE_PATH, pvpFromProbeBody, pvpFromQuery } from '../../net/live-chat';

/** The two mounted surfaces (`server-config`'s `GameMode`). */
export type GameMode = 'live' | 'rounds';

/** The query parameter carrying an explicit override. */
export const GAME_MODE_QUERY_PARAM = 'mode';

/** The mode a probe cannot decide. Live, always: see the module note. */
export const FALLBACK_GAME_MODE: GameMode = 'live';

/** How long the probe waits before giving up and falling back. */
export const GAME_MODE_PROBE_TIMEOUT_MS = 4_000;

function asGameMode(value: string | null | undefined): GameMode | null {
  const normalized = value?.trim().toLowerCase();
  return normalized === 'live' || normalized === 'rounds' ? normalized : null;
}

/** The explicit `?mode=` override, or `null` when absent/unrecognised. Accepts a full query
 * string (`'?mode=rounds'`) or a bare one; a value that is neither mode is ignored rather than
 * treated as an error, so a stray parameter cannot strand a player on a blank screen. */
export function gameModeFromQuery(search: string): GameMode | null {
  try {
    return asGameMode(new URLSearchParams(search).get(GAME_MODE_QUERY_PARAM));
  } catch {
    return null;
  }
}

/** The build-time `VITE_GAME_MODE`, or `null` when unset/unrecognised. */
export function gameModeFromConfig(configured: string | undefined): GameMode | null {
  return asGameMode(configured);
}

export interface RoundsSurfaceProbe {
  /** The API origin — `''` for same-origin, which is what production uses. */
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}

/**
 * Probes for the rounds surface: does this server mount `/rounds/session`?
 *
 * Answered with `true` for **any** response other than `404`, because "the route is not mounted"
 * is the only thing a live server can say about a rounds path. A rounds server answers `405`
 * (the route is POST-only), which is a positive signal and costs nothing: no session is minted,
 * no sandbox is opened, no state changes.
 */
export async function probeRoundsSurface(probe: RoundsSurfaceProbe = {}): Promise<boolean> {
  const fetchImpl = probe.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const url = `${(probe.baseUrl ?? '').replace(/\/+$/, '')}/rounds/session`;
  const timeoutMs = probe.timeoutMs ?? GAME_MODE_PROBE_TIMEOUT_MS;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const answer = await Promise.race([
      fetchImpl(url, { method: 'GET' }),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), timeoutMs);
      }),
    ]);
    return answer !== null && answer.status !== 404;
  } catch {
    // Offline, DNS, CORS, a socket reset: inconclusive, so the caller falls back to live.
    return false;
  } finally {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  }
}

export interface GameModeResolution extends RoundsSurfaceProbe {
  /** `window.location.search` in production. */
  search?: string;
  /** `import.meta.env.VITE_GAME_MODE` in production. */
  configured?: string | undefined;
}

/** Resolves the mode from the three signals in the module note, in order. */
export async function resolveGameMode(options: GameModeResolution = {}): Promise<GameMode> {
  const explicit = gameModeFromQuery(options.search ?? '') ?? gameModeFromConfig(options.configured);
  if (explicit !== null) {
    return explicit;
  }
  return (await probeRoundsSurface(options)) ? 'rounds' : FALLBACK_GAME_MODE;
}

// --- the pvp prototype's discovery, chained onto this probe (`docs/dev/pvp-prototype.md`) ------
//
// The prototype needs one more boot-time fact — does this live server advertise pvp? The server
// carries it on `GET /health` (`waggle-server/src/router.rs`), so it costs a second request; the
// rule that keeps that honest is that it is asked **only where a request was going to be made
// anyway**. An explicitly-selected mode (`?mode=live`, `VITE_GAME_MODE`) probes *nothing*, and
// that invariant is this module's whole shape — so the chain below hangs off the probe's own
// result, not off the resolution. A rounds surface is never asked at all.
//
// The flag's *endpoint and name* are not here — they live in `net/live-chat.ts` with everything
// else about the prototype, so deleting the prototype does not leave stray constants in this file.

export interface BootSurface {
  mode: GameMode;
  /** The live server advertises the pvp prototype. Always `false` for a rounds surface, and for
   * any resolution that skipped the probe (unless `?pvp=` says otherwise). */
  pvp: boolean;
}

/** Asks the live surface whether it advertises the pvp prototype. Never throws; see
 * `net/live-chat.ts`'s note for why every unclear answer is `false`. */
async function probePvpFlag(options: GameModeResolution): Promise<boolean> {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const url = `${(options.baseUrl ?? '').replace(/\/+$/, '')}${PVP_PROBE_PATH}`;
  try {
    const answer = await fetchImpl(url, { method: 'GET' });
    return answer.ok && pvpFromProbeBody(await answer.json());
  } catch {
    return false;
  }
}

/**
 * [`resolveGameMode`], plus the pvp prototype's flag.
 *
 * Behaviourally identical to `resolveGameMode` in every respect that decides the mode: the same
 * three signals in the same order, the same "an inconclusive probe means live" fallback, and the
 * same **"an explicit selection makes no request"** guarantee — which is why the pvp read is
 * chained after the probe rather than run beside it.
 */
export async function resolveBootSurface(options: GameModeResolution = {}): Promise<BootSurface> {
  const search = options.search ?? '';
  const override = pvpFromQuery(search);
  const explicit = gameModeFromQuery(search) ?? gameModeFromConfig(options.configured);
  if (explicit !== null) {
    // No request is made here, deliberately — so the only pvp signal available is the override.
    return { mode: explicit, pvp: explicit === 'live' && override === true };
  }

  const mode = (await probeRoundsSurface(options)) ? 'rounds' : FALLBACK_GAME_MODE;
  if (mode === 'rounds') {
    // A rounds surface has no pvp prototype, and is never asked.
    return { mode, pvp: false };
  }
  return { mode, pvp: override ?? (await probePvpFlag(options)) };
}
